import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Globe, 
  ArrowLeft, 
  RotateCcw, 
  Search, 
  CheckCircle2, 
  TrendingUp,
  ExternalLink,
  ShieldAlert,
  BarChart2,
  Award
} from 'lucide-react';

export const BacklinkChecker = () => {
  const { withAd, toast } = useAdRedirect();
  const [domainInput, setDomainInput] = useState('toolmetrico.com');
  const [activeDomain, setActiveDomain] = useState('toolmetrico.com');

  const domainDataPool = {
    'toolmetrico.com': {
      da: 58,
      pa: 62,
      totalBacklinks: '14,820',
      referringDomains: '1,240',
      doFollowScore: '84%',
      trustFlow: 45,
      citationFlow: 52,
      topAnchors: [
        { text: 'toolmetrico suite', count: '4,210', percentage: '28%' },
        { text: 'free developer tools', count: '3,150', percentage: '21%' },
        { text: 'toolmetrico.com', count: '2,890', percentage: '19%' },
        { text: 'image resizer online', count: '1,560', percentage: '10%' },
        { text: 'pdf utilities', count: '1,210', percentage: '8%' }
      ]
    },
    'github.com': {
      da: 98,
      pa: 99,
      totalBacklinks: '1.2B',
      referringDomains: '12.4M',
      doFollowScore: '92%',
      trustFlow: 94,
      citationFlow: 96,
      topAnchors: [
        { text: 'github', count: '450M', percentage: '38%' },
        { text: 'view on github', count: '280M', percentage: '23%' },
        { text: 'source code', count: '190M', percentage: '16%' }
      ]
    },
    'react.dev': {
      da: 92,
      pa: 94,
      totalBacklinks: '45.2M',
      referringDomains: '850K',
      doFollowScore: '88%',
      trustFlow: 82,
      citationFlow: 89,
      topAnchors: [
        { text: 'react documentation', count: '12M', percentage: '26%' },
        { text: 'react.js', count: '9.8M', percentage: '21%' },
        { text: 'learn react', count: '6.5M', percentage: '14%' }
      ]
    }
  };

  const generateProfile = (e) => {
    if (e) e.preventDefault();
    const query = domainInput.toLowerCase().trim().replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/.*$/, '');
    if (!query) return;
    setActiveDomain(query);
  };

  const currentProfile = domainDataPool[activeDomain] || {
    da: Math.floor(Math.random() * 40) + 10,
    pa: Math.floor(Math.random() * 45) + 12,
    totalBacklinks: (Math.floor(Math.random() * 900) + 50) + '',
    referringDomains: (Math.floor(Math.random() * 150) + 15) + '',
    doFollowScore: (Math.floor(Math.random() * 30) + 60) + '%',
    trustFlow: Math.floor(Math.random() * 35) + 10,
    citationFlow: Math.floor(Math.random() * 40) + 15,
    topAnchors: [
      { text: activeDomain, count: '120', percentage: '45%' },
      { text: 'website link', count: '85', percentage: '32%' },
      { text: 'click here', count: '40', percentage: '15%' }
    ]
  };

  const handleReset = () => {
    setDomainInput('');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Backlink Checker & Domain Profiler" 
        description="Simulate domain authority, trust flow, referring domains, and top anchor texts instantly. Comprehensive client-side backlink audit tool."
        keywords="backlink checker, domain authority check, trust flow simulator, referring domains, anchor text analysis, seo backlink tool"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Backlink Checker</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Backlink Authority Checker & Domain Profiler
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Analyze simulated domain authority metrics, referring link profiles, and anchor text distribution footprints for strategic SEO link-building.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Input Domain */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={18} style={{ color: '#0284c7' }} /> Inspect Target Domain
            </h3>

            <form onSubmit={generateProfile} style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input 
                  type="text" 
                  value={domainInput} 
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="e.g. toolmetrico.com, github.com, react.dev" 
                  style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: '1px solid var(--border-color)', fontSize: '1rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
                />
              </div>

              <button 
                type="submit" 
                style={{ background: '#0284c7', color: '#ffffff', border: 'none', padding: '0 24px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
              >
                Profile Domain
              </button>
            </form>

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Sample Profiles:</span>
              {['toolmetrico.com', 'github.com', 'react.dev'].map(preset => (
                <button 
                  key={preset}
                  type="button" 
                  onClick={() => { setDomainInput(preset); setTimeout(() => setActiveDomain(preset), 50); }}
                  style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={18} style={{ color: '#10b981' }} /> Authority Flow Metrics
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Trust Flow (TF)</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981' }}>{currentProfile.trustFlow}</div>
                </div>
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '8px', borderRadius: '8px', color: '#10b981' }}>
                  <TrendingUp size={20} />
                </div>
              </div>

              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Citation Flow (CF)</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0284c7' }}>{currentProfile.citationFlow}</div>
                </div>
                <div style={{ background: 'rgba(2, 132, 199, 0.1)', padding: '8px', borderRadius: '8px', color: '#0284c7' }}>
                  <BarChart2 size={20} />
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Right Side: Backlink Profile Stats */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '20px' }}>
              <div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Globe size={18} style={{ color: 'var(--primary)' }} /> Domain Profile Overview
                </h3>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#0284c7' }}>{activeDomain}</span>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '2px' }}>DA Score</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981' }}>{currentProfile.da}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '2px' }}>PA Score</div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0284c7' }}>{currentProfile.pa}</div>
                </div>
              </div>
            </div>

            {/* Middle Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Backlinks</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)' }}>{currentProfile.totalBacklinks}</div>
              </div>

              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Referring Domains</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)' }}>{currentProfile.referringDomains}</div>
              </div>

              <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>DoFollow %</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10b981' }}>{currentProfile.doFollowScore}</div>
              </div>
            </div>

            {/* Top Anchor Texts Breakdown */}
            <div>
              <h4 style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TrendingUp size={16} style={{ color: 'var(--primary)' }} /> Top Anchor Text Distributions
              </h4>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {currentProfile.topAnchors.map((anchor, idx) => (
                  <div key={idx} style={{ background: 'var(--bg-secondary)', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: 700, color: 'var(--text-primary)', background: '#ffffff', padding: '2px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>#{idx+1}</span>
                      <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>&quot;{anchor.text}&quot;</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{anchor.count} links</span>
                      <span style={{ background: 'rgba(5, 117, 251, 0.1)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '6px', fontWeight: 700, fontSize: '0.75rem' }}>{anchor.percentage}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginTop: '28px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('backlink-export', () => alert("Audit Export Complete! Check your downloads."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Export Full Backlink PDF Report
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="backlink-checker" />
    </div>
  );
};

export default BacklinkChecker;
